/**
 * O que todos os modulos usam: o envelope e os erros do contrato, o dinheiro,
 * o endereco, os parametros de listagem e a referencia a uma parte.
 *
 * O cliente HTTP, com a paginacao (`requestPage`), fica em `../client.ts`.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

export interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  request_id?: string;
}

export interface ErrorEnvelope {
  error: ApiErrorBody;
}

/** Codigos de erro estaveis usados na interface. */
export const ErrorCode = {
  Unauthenticated: 'UNAUTHENTICATED',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  SessionExpired: 'SESSION_EXPIRED',
  SessionRevoked: 'SESSION_REVOKED',
  RefreshTokenReused: 'REFRESH_TOKEN_REUSED',
  AccountLocked: 'ACCOUNT_LOCKED',
  PermissionDenied: 'PERMISSION_DENIED',
  FeatureNotEnabled: 'FEATURE_NOT_ENABLED',
  TenantInactive: 'TENANT_INACTIVE',
  NotFound: 'RESOURCE_NOT_FOUND',
  Conflict: 'RESOURCE_CONFLICT',
  ValidationFailed: 'VALIDATION_FAILED',
  RateLimited: 'RATE_LIMITED',
  Internal: 'INTERNAL_ERROR',
  NetworkUnavailable: 'NETWORK_UNAVAILABLE',
  BackupDisabled: 'BACKUP_DESLIGADO',
  BackupDirMissing: 'BACKUP_DIRETORIO_AUSENTE',
  // Triagem do cadastro do corretor na SUSEP (ADR 0095).
  BrokerSusepNotApt: 'CORRETORES_SUSEP_NAO_APTA',
  BrokerSusepUnavailable: 'CORRETORES_SUSEP_INDISPONIVEL',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * Valor monetario no transporte.
 *
 * O montante viaja como inteiro na menor unidade da moeda, com a moeda e o
 * expoente ao lado. Ver docs/API.md secao 8: assim nenhum cliente precisa supor
 * quantas casas a moeda tem, nem interpretar ponto flutuante.
 */
export interface Money {
  amount: number;
  currency: string;
  exponent: number;
}

export interface Address {
  zip_code: string;
  street: string;
  number: string;
  /** Complemento. Opcional no contrato para o cadastro antigo que nao o enviava. */
  complement?: string;
  district: string;
  city: string;
  state: string;
  /**
   * Codigo IBGE do municipio (P-TOM-C1). So o tomador o grava: o corretor e as
   * partes da CCG nao o enviam, e o AddressFields so o preenche com `withCityCode`.
   */
  city_code?: string;
}

export const emptyAddress: Address = {
  zip_code: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
};

export interface ListParams {
  limit?: number;
  offset?: number;
  q?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Monta a consulta de uma listagem, sem o parametro vazio, falso ou indefinido.
 *
 * Exportado para os arquivos de modulo, e nao para as telas: `../resources.ts` nao o
 * reexporta, e o caminho de cada rota continua escrito so no modulo dela.
 */
export function query(params: ListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export interface PartyRef {
  id: string;
  label: string;
  sublabel: string;
}
